import { MathUtils } from 'three';

/**
 * @description utility functions for numbers, units, and precision
 */
export class NumUtils {
  /**
   * @description expects a value in mm unit, and converts it to meter without changing precision
   * @example mmToMeter(5123.452109840)
   * -> 5.123452109840
   */
  static mmToMeters(numInMm: number): number {
    return Math.round(numInMm + Number.EPSILON) / 1000;
  }

  /**
   * @description expects a value in mm. Converts it to meter, and rounds to mm precision
   * @example mmToMetersRoundedToMm(5123.852109840)
   * -> 5.124
   */
  static mmToMetersRoundedToMm(numInMm: number): number {
    return Math.round(numInMm + Number.EPSILON) / 1000;
  }

  /**
   * @description Expects a value in meter. Converts it to mm, and rounds
   * @example
   * metersToMmRounded(5.1567451)
   * -> 5157
   */
  static metersToMmRounded(numInMeter: number): number {
    return Math.round((numInMeter + +Number.EPSILON) * 1000);
  }

  /**
   * Normalizes user-entered decimal separators.
   *
   * Allows either comma or dot as decimal separator, but rejects inputs that contain both.
   *
   * @example
   * NumUtils.normalizeDecimalSeparator('1,23') // '1.23'
   * NumUtils.normalizeDecimalSeparator('1.23') // '1.23'
   * NumUtils.normalizeDecimalSeparator('1,23.4') // null
   */
  static normalizeDecimalSeparator(input: string): string | null {
    const hasComma = input.includes(',');
    const hasDot = input.includes('.');

    if (hasComma && hasDot) return null;
    return hasComma ? input.replaceAll(',', '.') : input;
  }

  /**
   * Parses a user-entered number string and returns a non-negative finite number or null.
   *
   * - Trims whitespace
   * - Allows comma OR dot as decimal separator (but not both)
   */
  static parseNonNegativeNumber(input: string): number | null {
    const trimmed = input.trim();
    if (!trimmed) return null;

    const normalized = this.normalizeDecimalSeparator(trimmed);
    if (normalized === null) return null;

    const value = Number(normalized);
    if (!Number.isFinite(value) || value < 0) return null;
    return value;
  }

  /**
   * @description Restricts a value to a circular range defined by a period. For example, with a period of 360, -10 becomes 350, and 370 becomes 10.
   * @example
   * NumUtils.restrictToCircularRange(-10, 360) // 350
   * NumUtils.restrictToCircularRange(370, 360) // 10
   */
  static restrictToCircularRange(value: number, period: number): number {
    return MathUtils.euclideanModulo(value, period);
  }

  static restrictAngleToRange0to360(angleInDegrees: number): number {
    return Math.round(this.restrictToCircularRange(angleInDegrees, 360));
  }
}
